(() => {
  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  };

  const initCountdowns = (root = document) => {
    root.querySelectorAll('[data-rove-campaign]').forEach((bar) => {
      if (bar.dataset.countdownInitialized) return;
      bar.dataset.countdownInitialized = 'true';
      const countdown = bar.querySelector('[data-rove-countdown]');
      const output = bar.querySelector('[data-rove-countdown-output]');
      const endValue = bar.dataset.endTime;
      if (!countdown || !output || !endValue) {
        if (countdown) countdown.hidden = true;
        return;
      }

      const end = new Date(endValue).getTime();
      if (!Number.isFinite(end)) {
        countdown.hidden = true;
        return;
      }

      let timer;
      const update = () => {
        const remaining = end - Date.now();
        if (remaining <= 0) {
          window.clearInterval(timer);
          countdown.hidden = true;
          if (bar.dataset.hideExpired === 'true') bar.hidden = true;
          return;
        }
        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600) + days * 24;
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        output.textContent = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
      };
      countdown.hidden = false;
      update();
      timer = window.setInterval(update, 1000);
    });
  };

  const initComparisons = (root = document) => {
    root.querySelectorAll('[data-rove-compare]').forEach((comparison) => {
      if (comparison.dataset.compareInitialized) return;
      comparison.dataset.compareInitialized = 'true';
      const range = comparison.querySelector('input[type="range"]');
      if (!range) return;
      const update = () => comparison.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', update);
      range.addEventListener('change', update);
      update();
    });
  };

  const initReels = (root = document) => {
    const videos = [...root.querySelectorAll('.rove-reel-media video')];
    if (!videos.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        const shouldAutoplay = video.closest('[data-rove-autoplay]')?.dataset.roveAutoplay === 'true';
        if (entry.isIntersecting && shouldAutoplay) {
          video.play().catch(() => {});
        } else if (!entry.isIntersecting) {
          video.pause();
        }
      });
    }, { rootMargin: '100px', threshold: 0.45 });
    videos.forEach((video) => {
      if (video.dataset.reelInitialized) return;
      video.dataset.reelInitialized = 'true';
      observer.observe(video);
    });
  };

  const initReveals = (root = document) => {
    const items = [...root.querySelectorAll('[data-rove-reveal]')];
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach((item) => observer.observe(item));
  };

  const setRoveGalleryColour = (gallery, requestedColour) => {
    if (!gallery) return;
    const colour = requestedColour === 'pink' ? 'pink' : 'black';
    gallery.dataset.colour = colour;
    gallery.querySelectorAll('[data-rove-gallery-set]').forEach((set) => {
      const isCurrent = set.dataset.roveGallerySet === colour;
      set.hidden = !isCurrent;
      if (isCurrent) {
        [...set.querySelectorAll('[data-rove-gallery-slide]')].forEach((slide, index) => {
          slide.hidden = index !== 0;
          slide.classList.toggle('is-active', index === 0);
        });
      }
    });
    gallery.querySelectorAll('[data-rove-gallery-thumb]').forEach((thumb) => {
      const isCurrentColour = thumb.dataset.roveGalleryThumb.startsWith(`${colour}-`);
      const isFirst = thumb.dataset.roveGalleryThumb === `${colour}-1`;
      thumb.hidden = !isCurrentColour;
      thumb.classList.toggle('is-active', isCurrentColour && isFirst);
      if (isCurrentColour && isFirst) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const label = gallery.querySelector('[data-rove-gallery-colour-label]');
    if (label) label.textContent = colour === 'pink' ? 'Blush pink' : 'Black';
  };

  const initProductGalleries = (root = document) => {
    root.querySelectorAll('[data-rove-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryInitialized) return;
      gallery.dataset.galleryInitialized = 'true';
      gallery.querySelectorAll('[data-rove-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const target = thumb.dataset.roveGalleryThumb;
          const colour = target.startsWith('pink-') ? 'pink' : 'black';
          gallery.querySelectorAll(`[data-rove-gallery-set="${colour}"] [data-rove-gallery-slide]`).forEach((slide) => {
            const isActive = slide.dataset.roveGallerySlide === target;
            slide.hidden = !isActive;
            slide.classList.toggle('is-active', isActive);
          });
          gallery.querySelectorAll(`[data-rove-gallery-thumb^="${colour}-"]`).forEach((candidate) => {
            const isActive = candidate === thumb;
            candidate.classList.toggle('is-active', isActive);
            if (isActive) candidate.setAttribute('aria-current', 'true');
            else candidate.removeAttribute('aria-current');
          });
        });
      });
      setRoveGalleryColour(gallery, gallery.dataset.colour);
    });
  };

  const initBundles = (root = document) => {
    root.querySelectorAll('[data-rove-bundle]').forEach((bundle) => {
      if (bundle.dataset.bundleInitialized) return;
      bundle.dataset.bundleInitialized = 'true';
      const form = document.getElementById(bundle.dataset.formId);
      const options = [...bundle.querySelectorAll('[data-rove-bundle-option]')];
      const variantRows = [...bundle.querySelectorAll('[data-rove-bundle-variant]')];
      const error = bundle.querySelector('[data-rove-bundle-error]');
      if (!form || !options.length) return;

      const selectedOption = () => options.find((option) => option.querySelector('input').checked) || options[0];
      const sync = () => {
        const option = selectedOption();
        const quantity = Number(option.dataset.quantity || 1);
        options.forEach((candidate) => candidate.classList.toggle('is-selected', candidate === option));
        variantRows.forEach((row, index) => { row.hidden = index >= quantity; });
        const firstSelect = variantRows[0]?.querySelector('select');
        const selectedVariant = firstSelect?.value;
        const formVariantInput = form.querySelector('[name="id"]');
        if (selectedVariant && formVariantInput) formVariantInput.value = selectedVariant;
        const colour = firstSelect?.selectedOptions[0]?.dataset.colour || 'black';
        const productRoot = bundle.closest('product-info') || document;
        productRoot.querySelectorAll('[data-rove-product-gallery]').forEach((gallery) => setRoveGalleryColour(gallery, colour));
        bundle.dispatchEvent(new CustomEvent('rove:bundlechange', { bubbles: true, detail: { quantity, colour } }));
      };

      options.forEach((option) => option.querySelector('input').addEventListener('change', sync));
      variantRows.forEach((row) => row.querySelector('select')?.addEventListener('change', sync));
      sync();

      form.addEventListener('submit', async (event) => {
        const option = selectedOption();
        const quantity = Number(option.dataset.quantity || 1);
        event.preventDefault();
        event.stopImmediatePropagation();
        if (error) { error.hidden = true; error.textContent = ''; }

        const submitButton = form.querySelector('[type="submit"]');
        const spinner = submitButton?.querySelector('.loading__spinner');
        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          const selectedIds = variantRows.slice(0, quantity).map((row) => Number(row.querySelector('select').value));
          const grouped = selectedIds.reduce((accumulator, id) => {
            accumulator[id] = (accumulator[id] || 0) + 1;
            return accumulator;
          }, {});
          const items = Object.entries(grouped).map(([id, itemQuantity]) => ({ id: Number(id), quantity: itemQuantity }));
          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          const payload = { items };
          if (cart?.getSectionsToRender) {
            payload.sections = cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            cart.setActiveElement?.(document.activeElement);
          }
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(payload),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this set to your bag.');
          if (cart?.renderContents) {
            cart.renderContents(result);
          } else {
            window.location.assign(window.routes.cart_url);
          }
        } catch (exception) {
          if (error) { error.textContent = exception.message; error.hidden = false; }
        } finally {
          submitButton?.removeAttribute('aria-disabled');
          submitButton?.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }, true);
    });
  };

  const initSticky = (root = document) => {
    root.querySelectorAll('[data-rove-sticky]').forEach((bar) => {
      if (bar.dataset.stickyInitialized) return;
      bar.dataset.stickyInitialized = 'true';
      document.body.classList.add('rove-has-sticky-atc');
      const mainButton = document.querySelector('.product-form__submit[name="add"]');
      const stickyButton = bar.querySelector('[data-rove-sticky-button]');
      if (!mainButton || !stickyButton) return;
      const stickyPrice = bar.querySelector('[data-rove-sticky-price]');
      const stickyImage = bar.querySelector('.rove-sticky-product img');
      const productInfo = mainButton.closest('[id^="ProductInfo-"]');
      const sync = () => {
        stickyButton.disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
        const label = mainButton.querySelector('span')?.textContent?.trim();
        if (label) stickyButton.textContent = label;
        const currentPrice = productInfo?.querySelector('.price .price-item--sale, .price .price-item--regular')?.textContent?.trim();
        if (stickyPrice && currentPrice) stickyPrice.textContent = currentPrice;
        const activeMediaImage = document.querySelector('[data-rove-product-gallery] .rove-product-gallery__slide.is-active img, media-gallery .product__media-item.is-active img');
        if (stickyImage && activeMediaImage) {
          stickyImage.src = activeMediaImage.currentSrc || activeMediaImage.src;
          stickyImage.srcset = '';
          stickyImage.alt = activeMediaImage.alt || '';
        }
      };
      const observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 280);
      }, { threshold: 0.05 });
      observer.observe(mainButton);
      new MutationObserver(sync).observe(productInfo || mainButton, { attributes: true, subtree: true, childList: true, characterData: true });
      const mediaGallery = document.querySelector('media-gallery');
      if (mediaGallery) new MutationObserver(sync).observe(mediaGallery, { attributes: true, attributeFilter: ['class'], subtree: true });
      stickyButton.addEventListener('click', () => mainButton.click());
      sync();
    });
  };

  const initHomeOffer = (root = document) => {
    root.querySelectorAll('[data-rove-home-offer]').forEach((section) => {
      if (section.dataset.homeOfferInitialized) return;
      section.dataset.homeOfferInitialized = 'true';
      const select = section.querySelector('select[name="id"]');
      const price = section.querySelector('[data-rove-home-price]');
      if (!select || !price) return;
      const update = () => {
        const option = select.selectedOptions[0];
        if (option?.dataset.price) price.textContent = money(Number(option.dataset.price), section.dataset.currency);
      };
      select.addEventListener('change', update);
      update();
    });
  };

  const init = (root = document) => {
    initCountdowns(root);
    initComparisons(root);
    initReels(root);
    initReveals(root);
    initProductGalleries(root);
    initBundles(root);
    initSticky(root);
    initHomeOffer(root);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
